<template>
  <div class="interval-card">
    <div class="total-time">
      <div>
        <v-icon color="#A0AEC0" size="16">mdi-clock-outline</v-icon>
        <span>Intervalo</span>
        <span v-if="interval?.id === taskIntervalActive?.id">(pause o a tarefa para editar)</span>
        <p class="mb-0" :class="{ 'pulsing-clock': interval?.id === taskIntervalActive?.id }">
          {{ totalIntervalTime }}
        </p>
      </div>
      <v-icon color="#F56565" size="20" :disabled="disabledInputs" @click="openModalConfirm">mdi-trash-can-outline</v-icon>
    </div>
    <div class="d-flex mt-5" style="gap: 10px">
      <v-text-field
        v-model="startTime"
        v-debounce:2s="updateTaskInterval"
        label="Início"
        height="50"
        dense
        outlined
        hide-details
        persistent-placeholder
        :disabled="disabledInputs"
        @input="formatTimeInput('startTime')"
      />
      <v-text-field
        v-model="endTime"
        v-debounce:2s="updateTaskInterval"
        label="Fim"
        height="50"
        dense
        outlined
        hide-details
        persistent-placeholder
        :disabled="disabledInputs"
        @input="formatTimeInput('endTime')"
      />
    </div>
    <div class="interval-date mt-3">
      <span>{{ formatDate(interval.created_at) }}</span>
      <v-icon color="#A0AEC0" size="16">mdi-calendar-month-outline</v-icon>
    </div>

    <ModalConfirmDeleteInterval :is-active="isModalConfirmActive" @close="closeModalConfirm" @confirm-delete="deleteTaskInterval" />
  </div>
</template>

<script>
import TaskService from '@/services/api/task';
import ModalConfirmDeleteInterval from './ModalConfirmDeleteInterval';

export default {
  name: 'TaskIntervalCard',
  components: { ModalConfirmDeleteInterval },
  props: {
    interval: {
      type: Object,
      required: true
    },
    taskId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      isModalConfirmActive: false,
      startTime: '',
      endTime: ''
    };
  },
  computed: {
    taskToShowIntervalsHistory() {
      return this.$store.getters['ServiceOrderTask/getTaskToShowIntervalsHistory'];
    },

    disabledInputs() {
      return this.interval.id === this.taskIntervalActive?.id || this.taskToShowIntervalsHistory.status === 'finished';
    },
    taskIntervalActive() {
      return this.$store.getters['ServiceOrderTask/getServiceOrderTaskIntervalActive'];
    },
    taskIntervalActiveTotalTime() {
      return this.$store.getters['ServiceOrderTask/getTaskIntervalActiveTotalTime'];
    },
    totalIntervalTime() {
      let totalMilliseconds = 0;

      if (this.interval.id === this.taskIntervalActive?.id) {
        totalMilliseconds = this.taskIntervalActiveTotalTime;
      } else {
        const startDate = new Date(this.interval.start_date);
        const endDate = new Date(this.interval.end_date);
        totalMilliseconds = endDate - startDate;
      }

      const totalMinutes = Math.floor(totalMilliseconds / 60000);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;

      return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }
  },
  methods: {
    openModalConfirm() {
      this.isModalConfirmActive = true;
    },
    closeModalConfirm() {
      this.isModalConfirmActive = false;
    },
    async updateTaskInterval() {
      if (!this.isValidTime(this.startTime) || !this.isValidTime(this.endTime)) {
        this.$toast.error('Horário inválido. Use o formato hh:mm.');
        return;
      }

      if (!this.isStartTimeBeforeEndTime(this.startTime, this.endTime)) {
        this.$toast.error('O horário de início deve ser antes do horário de fim.');
        return;
      }

      const body = {
        taskId: this.taskId,
        intervalId: this.interval.id
      };

      if (this.startTime && this.startTime !== this.formatTime(this.interval.start_date)) {
        body.start_date = this.mergeDateAndTime(this.interval.start_date, this.startTime);
      }

      if (this.endTime && this.endTime !== this.formatTime(this.interval.end_date)) {
        body.end_date = this.mergeDateAndTime(this.interval.end_date, this.endTime);
      }

      try {
        this.$store.dispatch('ServiceOrderTask/setLoading', true);
        const response = await TaskService.updateTaskInterval(this.taskId, this.interval.id, body);

        this.$store.dispatch('ServiceOrderTask/setTaskToShowIntervalsHistory', response.data);
        this.$store.dispatch('ServiceOrderTask/setTask', { task: response.data });
      } catch (error) {
        console.error(error);
        this.$toast.error('Erro ao atualizar intervalo');
      } finally {
        this.$store.dispatch('ServiceOrderTask/setLoading', false);
      }
    },
    deleteTaskInterval() {
      this.$store.dispatch('ServiceOrderTask/deleteTaskInterval', { taskId: this.taskId, intervalId: this.interval.id });
    },
    formatDate(date) {
      return new Date(date).toLocaleDateString('pt-BR');
    },
    formatTime(date) {
      if (!date) return '';
      const d = new Date(date);
      const hours = d.getHours().toString().padStart(2, '0');
      const minutes = d.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    },
    formatTimeInput(field) {
      const time = this[field];
      if (!time) return;

      const formattedTime = time.replace(/[^0-9]/g, '').slice(0, 4);
      if (formattedTime.length >= 3) {
        this[field] = `${formattedTime.slice(0, 2)}:${formattedTime.slice(2, 4)}`;
      } else if (formattedTime.length >= 1) {
        this[field] = `${formattedTime.slice(0, 2)}`;
      }
    },
    isValidTime(time) {
      const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
      return timePattern.test(time);
    },
    isStartTimeBeforeEndTime(startTime, endTime) {
      const [startHours, startMinutes] = startTime.split(':').map(Number);
      const [endHours, endMinutes] = endTime.split(':').map(Number);

      if (startHours < endHours) {
        return true;
      }
      if (startHours === endHours) {
        return startMinutes < endMinutes;
      }
      return false;
    },
    mergeDateAndTime(date, time) {
      const [hours, minutes] = time.split(':').map(Number);
      const newDate = new Date(date);
      newDate.setHours(hours, minutes, 0, 0);
      return newDate.toISOString();
    }
  },
  mounted() {
    this.startTime = this.formatTime(this.interval.start_date);
    this.endTime = this.formatTime(this.interval.end_date);
  }
};
</script>

<style lang="scss" scoped>
@keyframes pulsing-clock {
  0% {
    opacity: 0;
  }
  70% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}

.pulsing-clock {
  animation: pulsing-clock 1.5s infinite;
}

.interval-card {
  padding: 16px;
  background-color: #fff;
  border-radius: 8px;
  margin-top: 12px;
  border: 1px solid #e2e8f0;

  .total-time {
    display: flex;
    justify-content: space-between;
    align-items: center;
    span {
      margin-left: 4px;
      font-size: 14px;
      font-weight: 400;
      color: #718096;
    }

    p {
      font-size: 18px;
      font-weight: 700;
      color: #4a5568;
    }
  }

  .interval-date {
    display: flex;
    align-items: center;
    justify-content: flex-end;

    span {
      font-size: 14px;
      font-weight: 400;
      color: #718096;
      margin-right: 4px;
      line-height: 16px;
    }
  }
}
</style>
